import { useTranslation } from 'react-i18next'
import {
  CalendarDays,
  Combine,
  Trash2,
  ChevronsUpDown,
} from 'lucide-react'
import { ClarifyEntriesIcon } from '@/src/renderer/components/icons/ClarifyEntriesIcon'
import { DormantFilledIcon } from '@/src/renderer/components/icons/DormantIcon'
import { TaskDropdown } from '@/src/renderer/features/taskManagement/components/TaskDropdown'
import { WhenDropdownContent } from '@/src/renderer/features/taskManagement/components/WhenDropdown/WhenDropdown'
import { TodayIcon, TomorrowIcon } from '@/src/renderer/features/taskManagement/components/WhenDropdown'
import { MovePicker, readLastMoveSpace, rememberMoveSpace } from '@/src/renderer/features/taskManagement/components/MoveMenu'
import type { LifeSpace, Project, Task, StartPeriod } from '@/src/renderer/lib/db/models'
import type { MoveTarget } from '@/src/renderer/features/taskManagement/utils/moveTasks'
import type { StagedAction } from './useClarifyState'
import type { ClarifyPicker } from './useClarifyController'
import { formatRelativeAge } from './clarifyFormatters'
import './ClarifyActionPanel.scss'

export interface ClarifyActionPanelProps {
  activeTask: Task | null
  /**
   * If the active card already has a staged action, this carries its kind so
   * the corresponding button (Heute/Morgen/Demnächst/Ruhen/Verschieben/
   * Löschen) can render a selected state.
   */
  activeStagedAction?: StagedAction
  lifeSpace: 'personal' | 'work' | 'home'
  onStage: (action: StagedAction) => void
  /**
   * Which picker is open. Owned by the controller (#266): D, V and ⇧⌘M open
   * them, and while one is open H/M/R/L stay silent.
   */
  openPicker: ClarifyPicker | null
  onOpenPickerChange: (picker: ClarifyPicker | null) => void
}

/** Small inline kbd-style badge matching the Figma shortcut chip. */
function ShortcutBadge({ label }: { label: string }) {
  return <span className="clarify-action-shortcut">{label}</span>
}

/**
 * Right-hand action panel of the Clarify-Mode (Figma node 3630:3559).
 *
 * Layout:
 *   - Header: folder icon + task title + "vor X Tagen"
 *   - Separator line
 *   - "Wohin damit?" label
 *   - Actions (5 rows + visual dot between Verschieben and Ruhen):
 *       1. [Heute] [Morgen]                          M
 *       2. [Demnächst … ⇅]                            D
 *       3. [Verschieben … ⇅]                          V
 *       ·   (group-separator dot)
 *       4. [Ruhen lassen]                             R
 *       5. [Löschen]                                  L
 */
export function ClarifyActionPanel({
  activeTask,
  activeStagedAction,
  lifeSpace,
  onStage,
  openPicker,
  onOpenPickerChange,
}: ClarifyActionPanelProps) {
  const selectedKind = activeStagedAction?.kind
  const selectedClass = (kind: StagedAction['kind']) =>
    selectedKind === kind ? 'clarify-action-button--selected' : ''
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'en' ? 'en' : 'de'

  const setLater = (open: boolean) => onOpenPickerChange(open ? 'later' : null)
  const setMove = (open: boolean) => onOpenPickerChange(open ? 'move' : null)

  if (!activeTask) {
    return (
      <div className="clarify-action-panel clarify-action-panel--empty">
        <div className="clarify-action-panel__empty-text">
          {t('quickNotes.clarify.emptyState')}
        </div>
      </div>
    )
  }

  const handleToday = () => onStage({ kind: 'today' })
  const handleTomorrow = () => onStage({ kind: 'tomorrow' })
  const handlePause = () => onStage({ kind: 'pause' })
  const handleDelete = () => onStage({ kind: 'delete' })

  const handleLaterChange = (value: {
    date: Date
    time: string | null
    period: StartPeriod | null
  }) => {
    onStage({
      kind: 'later',
      date: value.date,
      time: value.time,
      period: value.period,
    })
    setLater(false)
  }

  /*
    **Verschieben** (#267): dasselbe Panel wie an der Zeile (#263) — jeder Lebensbereich, Abschnitte,
    „Neues Projekt“. Hier nur vorgemerkt; ein neues Projekt entsteht erst beim Buchen (K).
  */
  const handleMoveSelect = (target: MoveTarget, place: string, project: Project) => {
    onStage({ kind: 'move', ...target, place, icon: project.icon ?? null, iconColor: project.iconColor ?? null })
    setMove(false)
  }

  const handleCreateProject = (project: { name: string; lifeSpace: LifeSpace }) => {
    onStage({ kind: 'newProject', ...project })
    setMove(false)
  }

  return (
    <div className="clarify-action-panel">
      <header className="clarify-action-panel__header">
        {/* Symbole mit „Verschieben …“ getauscht (Sven, 06.10.2026); die Farbe hängt an der Klasse. */}
        <Combine size={16} className="clarify-action-panel__header-icon" />
        <div className="clarify-action-panel__header-text">
          <div className="clarify-action-panel__title">{activeTask.title}</div>
          <div className="clarify-action-panel__age">
            {formatRelativeAge(activeTask.createdAt, locale)}
          </div>
        </div>
      </header>

      <div className="clarify-action-panel__separator" aria-hidden="true" />

      <div className="clarify-action-panel__actions">
        <div className="clarify-action-panel__row clarify-action-panel__row--split">
          <button
            type="button"
            className={`clarify-action-button ${selectedClass('today')}`.trim()}
            onClick={handleToday}
          >
            <span className="clarify-action-button__icon">
              <TodayIcon size={16} />
            </span>
            <span className="clarify-action-button__label">
              {t('quickNotes.clarify.actionToday')}
            </span>
          </button>
          <button
            type="button"
            className={`clarify-action-button ${selectedClass('tomorrow')}`.trim()}
            onClick={handleTomorrow}
          >
            <span className="clarify-action-button__icon">
              <TomorrowIcon size={16} />
            </span>
            <span className="clarify-action-button__label">
              {t('quickNotes.clarify.actionTomorrow')}
            </span>
          </button>
          <ShortcutBadge label="M" />
        </div>

        <div className="clarify-action-panel__row">
          <TaskDropdown
            open={openPicker === 'later'}
            onOpenChange={setLater}
            side="bottom"
            align="start"
            sideOffset={4}
            trigger={
              <button
                type="button"
                className={`clarify-action-button clarify-action-button--full clarify-action-button--with-chevron clarify-action-button--later ${selectedClass('later')}`.trim()}
              >
                <CalendarDays size={16} className="clarify-action-button__icon" />
                <span className="clarify-action-button__label">
                  {t('quickNotes.clarify.actionLater')}
                </span>
                <ChevronsUpDown
                  size={16}
                  className="clarify-action-button__chevron"
                />
              </button>
            }
          >
            <WhenDropdownContent
              value={null}
              onValueChange={handleLaterChange}
              onClose={() => setLater(false)}
              placement="actionbar"
            />
          </TaskDropdown>
          <ShortcutBadge label="D" />
        </div>

        <div className="clarify-action-panel__row">
          <TaskDropdown
            open={openPicker === 'move'}
            onOpenChange={setMove}
            side="bottom"
            align="start"
            sideOffset={4}
            trigger={
              <button
                type="button"
                className={`clarify-action-button clarify-action-button--full clarify-action-button--with-chevron clarify-action-button--move ${selectedClass('move') || selectedClass('newProject')}`.trim()}
              >
                <ClarifyEntriesIcon size={16} className="clarify-action-button__icon" />
                <span className="clarify-action-button__label">
                  {t('quickNotes.clarify.actionMove')}
                </span>
                <ChevronsUpDown
                  size={16}
                  className="clarify-action-button__chevron"
                />
              </button>
            }
          >
            <MovePicker
              title={t('taskItem.move.title', { title: activeTask.title })}
              startSpace={activeTask.lifeSpace ?? lifeSpace}
              // Derselbe Merker wie an der Zeile (#279).
              initialSpace={readLastMoveSpace()}
              onSpaceChange={rememberMoveSpace}
              current={{ projectId: activeTask.projectId, sectionId: activeTask.sectionId }}
              onSelect={handleMoveSelect}
              onCreateProject={handleCreateProject}
            />
          </TaskDropdown>
          <ShortcutBadge label="V" />
        </div>

        <div className="clarify-action-panel__group-separator" aria-hidden="true" />

        <div className="clarify-action-panel__row">
          <button
            type="button"
            className={`clarify-action-button clarify-action-button--full clarify-action-button--pause ${selectedClass('pause')}`.trim()}
            onClick={handlePause}
          >
            <span className="clarify-action-button__icon">
              <DormantFilledIcon size={16} />
            </span>
            <span className="clarify-action-button__label">
              {t('quickNotes.clarify.actionPause')}
            </span>
          </button>
          <ShortcutBadge label="R" />
        </div>

        <div className="clarify-action-panel__row">
          <button
            type="button"
            className={`clarify-action-button clarify-action-button--full clarify-action-button--destructive ${selectedClass('delete')}`.trim()}
            onClick={handleDelete}
          >
            <Trash2 size={16} className="clarify-action-button__icon" />
            <span className="clarify-action-button__label">
              {t('quickNotes.clarify.actionDelete')}
            </span>
          </button>
          <ShortcutBadge label="L" />
        </div>
      </div>
    </div>
  )
}
